// Role Form Component - Create/Edit Role

import { ArrowLeft, Save } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { supabase } from '../../../utils/supabaseClient';
import { Button } from '../../ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import type { Role } from './types';

type RoleFormProps = {
  role?: Role | null;
  tenantId: string;
  onClose: () => void;
  onSave: () => void;
};

export const RoleForm: React.FC<RoleFormProps> = ({
  role,
  tenantId,
  onClose,
  onSave,
}) => {
  const [roleName, setRoleName] = useState('');
  const [roleCode, setRoleCode] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (role) {
      setRoleName(role.roleName);
      setRoleCode(role.roleCode);
      setDescription(role.description || '');
    } else {
      setRoleName('');
      setRoleCode('');
      setDescription('');
    }
  }, [role]);

  const handleSave = async () => {
    if (!roleName.trim()) {
      alert('Please enter a role name');
      return;
    }

    setIsSaving(true);
    try {
      const generatedRoleCode =
        roleCode ||
        roleName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '');

      if (role) {
        // Update existing role
        const { error } = await supabase
          .from('base_roles')
          .update({
            role_name: roleName.trim(),
            role_code: generatedRoleCode,
            description: description.trim() || null,
            updated_at: new Date().toISOString(),
          })
          .eq('role_id', role.roleId)
          .eq('tenant_id', tenantId);

        if (error) throw error;
      } else {
        // Create new role
        const { error } = await supabase
          .from('base_roles')
          .insert({
            tenant_id: tenantId,
            role_name: roleName.trim(),
            role_code: generatedRoleCode,
            description: description.trim() || null,
            is_active: true,
          });

        if (error) throw error;
      }

      onSave();
    } catch (error) {
      console.error('Failed to save role:', error);
      alert('Failed to save role. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className='flex flex-col h-full'>
      <div className='border-b p-4'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-4'>
            <Button variant='ghost' size='sm' onClick={onClose}>
              <ArrowLeft className='h-4 w-4 mr-2' />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{role ? 'Edit Role' : 'Create Role'}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Define role details
              </p>
            </div>
          </div>
          <div className='flex items-center gap-2'>
            <Button variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving || !roleName.trim()}
            >
              <Save className='h-4 w-4 mr-2' />
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </div>

      <div className='flex-1 overflow-y-auto p-6'>
        <div className='max-w-4xl mx-auto space-y-6'>
          <Card>
            <CardHeader>
              <CardTitle>Role Details</CardTitle>
              <CardDescription>Basic information about the role. Permissions are configured via the Authority Matrix.</CardDescription>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div>
                <Label htmlFor='role-name'>Role Name *</Label>
                <Input
                  id='role-name'
                  placeholder='Enter role name'
                  value={roleName}
                  onChange={e => {
                    setRoleName(e.target.value);
                    if (!roleCode && !role) {
                      setRoleCode(
                        e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, '_')
                          .replace(/^_+|_+$/g, '')
                      );
                    }
                  }}
                  className='mt-1'
                />
              </div>
              <div>
                <Label htmlFor='role-code'>Role Code</Label>
                <Input
                  id='role-code'
                  placeholder='Auto-generated from role name'
                  value={roleCode}
                  onChange={e => setRoleCode(e.target.value)}
                  className='mt-1'
                />
                <p className='text-xs text-muted-foreground mt-1'>
                  Unique identifier for the role (auto-generated if not
                  provided)
                </p>
              </div>
              <div>
                <Label htmlFor='role-description'>Description</Label>
                <Textarea
                  id='role-description'
                  placeholder='Enter role description'
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  className='mt-1'
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
